import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import PersistentDataGrid from 'common/PersistentDataGrid';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import {
  CONSIGNMENT_STATUS_OPTIONS,
  INVENTORY_STATUS_OPTIONS,
} from 'feature/inventory-management/reconsignments/utils/constant';
import summaryColumns from './SummaryColumns';

const InfoSection = ({ title, value, error }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography color="textSecondary" variant="caption">
      {title}
    </Typography>
    <Typography
      sx={{
        color: error ? 'error.main' : 'inherit',
        fontWeight: 600,
      }}
      variant="caption"
    >
      {value}
    </Typography>
  </Box>
);

InfoSection.propTypes = {
  error: PropTypes.bool,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

InfoSection.defaultProps = {
  error: false,
  title: '',
  value: '',
};

const SummaryPage = ({
  reconsignmentState,
  selectedOrigin,
  totalTransferVolume,
  toLocations,
  locationsInventoryVolumes,
  formattedTransferDate,
}) => {
  const apiRef = useGridApiRef();
  const rows = R.pipe(
    R.filter(
      R.pipe(R.prop('transferVolume'), R.both(R.complement(R.isNil), R.complement(R.isEmpty)))
    ),
    R.map(row => ({
      fromLocation: `${reconsignmentState?.fromLocation?.locationIdn} ${reconsignmentState?.fromLocation?.locationName}`,
      locationId: row.locationId,
      toLocation: `${row.locationIdName} ${row.locationName}`,
      transferDate: reconsignmentState?.transferDate,
      transferVolume: row.transferVolume,
    }))
  )(toLocations);

  const consignmentStatusCode = R.pipe(
    R.propOr('', 'consignmentStatus'),
    R.toUpper
  )(reconsignmentState);
  const consignmentStatuLabel = R.pipe(
    R.find(R.propEq('code', consignmentStatusCode)),
    R.propOr(consignmentStatusCode, 'label')
  )(CONSIGNMENT_STATUS_OPTIONS);

  const inventoryStatusCode = R.pipe(
    R.propOr('', 'inventoryStatus'),
    R.toUpper
  )(reconsignmentState);
  const inventoryStatusLabel = R.pipe(
    R.find(R.propEq('code', inventoryStatusCode)),
    R.propOr(inventoryStatusCode, 'label')
  )(INVENTORY_STATUS_OPTIONS);

  return (
    <Box sx={{ mb: 7, p: 3, pt: 8 }}>
      <Box
        alignItems="start"
        columnGap={4}
        display="grid"
        gridTemplateColumns="1fr 1fr"
        mb={3}
        rowGap={1}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography
            sx={{
              flexWrap: 'nowrap',
              fontFamily: 'Overpass',
              fontSize: '24px',
              fontWeight: 400,
              lineHeight: 'normal',
              whiteSpace: 'nowrap',
            }}
            variant="h5"
          >
            Review Reconsignment
          </Typography>
          <Typography
            color="textSecondary"
            sx={{
              flexWrap: 'nowrap',
              fontFamily: 'Gothic A1',
              fontSize: '12.5px',
              fontWeight: 600,
              letterSpacing: '0.4px',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
            variant="body2"
          >
            Review the reconsignment prior to submitting
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              maxWidth: '100%',
              mt: 0.5,
              overflow: 'visible',
              whiteSpace: 'normal',
            }}
          >
            {selectedOrigin?.map(origin => (
              <Chip
                key={R.prop('originId', origin)}
                label={
                  <>
                    <strong>ORIGIN:</strong> {R.prop('originName', origin)}
                  </>
                }
                sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
                variant="outlined"
              />
            ))}
            {R.prop('rvp', reconsignmentState) && (
              <Chip
                label={
                  <>
                    <strong>RVP:</strong>{' '}
                    {R.is(String, R.prop('rvp', reconsignmentState))
                      ? R.toUpper(R.prop('rvp', reconsignmentState))
                      : R.prop('rvp', reconsignmentState)}
                  </>
                }
                sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
                variant="outlined"
              />
            )}
            <Chip
              label={
                <>
                  <strong>INVENTORY STATUS:</strong> {(inventoryStatusLabel || '').toUpperCase()}
                </>
              }
              sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
              variant="outlined"
            />
            <Chip
              label={
                <>
                  <strong>CONSIGNMENT STATUS:</strong> {(consignmentStatuLabel || '').toUpperCase()}
                </>
              }
              sx={{ borderColor: 'secondary.main', borderRadius: 25, textTransform: 'uppercase' }}
              variant="outlined"
            />
          </Box>
        </Box>
        <Box
          alignItems="center"
          display="flex"
          gap={6}
          sx={{
            '& > *': {
              flexShrink: 0,
            },
            flexWrap: 'nowrap',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
          }}
        >
          <InfoSection
            title="From Location"
            value={`${reconsignmentState?.fromLocation?.locationIdn} ${reconsignmentState?.locationName}`}
          />
          <InfoSection
            title="Product ID"
            value={R.pipe(
              R.pathOr('', ['product', 'productName']),
              R.split(' '),
              R.head,
              R.defaultTo('')
            )(reconsignmentState)}
          />
          <InfoSection title="Transfer Date" value={formattedTransferDate} />
          <InfoSection
            title="Available Volume"
            value={`${formatNumberWithoutDecimals(
              locationsInventoryVolumes?.availableVolume
            )} bbls`}
          />
          <InfoSection
            title="Intransit Volume"
            value={`${formatNumberWithoutDecimals(
              locationsInventoryVolumes?.inTransitVolume
            )} bbls`}
          />
          <InfoSection
            title="Total Transfer Volume"
            value={`${formatNumberWithoutDecimals(totalTransferVolume)} bbls`}
          />
        </Box>
      </Box>
      <Divider sx={{ mb: { lg: 5, xs: 3 }, mt: { lg: 4, xs: 3 } }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <PersistentDataGrid
          apiRef={apiRef}
          columns={summaryColumns}
          disableColumnMenu
          getRowId={R.prop('locationId')}
          getRowSpacing={() => ({
            bottom: 0,
            top: 0,
          })}
          hideFooter
          id="summaryGrid"
          initialState={{
            aggregation: {
              model: {
                transferVolume: 'sum',
              },
            },
            density: 'compact',
          }}
          rows={rows}
          slots={{
            toolbar: () => <></>,
          }}
        />
      </Box>
    </Box>
  );
};

SummaryPage.propTypes = {
  formattedTransferDate: PropTypes.shape({}).isRequired,
  locationsInventoryVolumes: PropTypes.shape({
    availableVolume: PropTypes.number,
    inTransitVolume: PropTypes.number,
  }).isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({
      locationIdn: PropTypes.string,
      locationName: PropTypes.string,
    }),
    locationName: PropTypes.string,
    transferDate: PropTypes.string,
  }).isRequired,
  selectedOrigin: PropTypes.shape([]).isRequired,
  toLocations: PropTypes.shape([]).isRequired,
  totalTransferVolume: PropTypes.number.isRequired,
};

export default SummaryPage;
